import React, { useContext, useState, useEffect, useRef } from 'react'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useHistory } from 'react-router-dom'

import { GlobalContext, UserContext } from 'components/context'
import { ActionButton } from 'components/Buttons'
import { currentActiveTypeFromKeys, getDeviceType, statusIsInactive } from 'helpers'
import { AppleButton, GoogleButton, QrCodeSvg } from './components'
import { StandardErrorPage } from 'components/DefaultPages'

const ActivateNFC = () => {
  const { membership } = useContext(UserContext)
  const { t, locale } = useContext(GlobalContext)
  const { get, put } = useContext(UserContext)
  const { logEvent } = useContext(UserContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [token, setToken] = useState(null)
  const [section, setSection] = useState('activate')
  const [selectedWallet, setSelectedWallet] = useState(null)
  const [success, setSuccess] = useState(null) // eslint-disable-line no-unused-vars
  const [error, setError] = useState(null) // eslint-disable-line no-unused-vars
  const history = useHistory()

  const pollTimeoutRef = useRef(null)
  const pollStoppedRef = useRef(true)
  const maxPolls = 30

  const country = locale?.size > 2 ? locale.substring(3).toLowerCase() : 'no'
  const device = getDeviceType()
  const rfid = currentActiveTypeFromKeys(membership.keys, 'rfid')
  const nfc = currentActiveTypeFromKeys(membership.keys, 'nfc')

  useEffect(() => {
    return () => stopPolling()
  }, [])

  if (!membership) {
    console.warn('no membership...')
    return null
  }

  // // TODO: Display error about status or redirect?
  if (statusIsInactive(membership.status)) {
    console.warn('invalid status...')
    return null
  }

  const prefix = 'pages.membership.access.nfc'

  const phrase = (suffix) => {
    if (Array.isArray(suffix)) {
      suffix = suffix.join('.')
    }
    return `${prefix}.${suffix}`
  }

  const onSubmit = (e) => {
    e.preventDefault()

    if (isSubmitting) return

    // If we already have a token, and it is for the same wallet type as chosen just use that rather than fetching again
    if (token && token.pass && token.pass.wallet_type === selectedWallet) {
      setSection(token.pass.link_type === 'short' ? 'redirect' : 'download')
      return
    }

    setIsSubmitting(true)

    if (device.isMobile) {
      const payload = {
        notify_method: 'direct',
        wallet_type: e.nativeEvent.submitter.value,
      }

      // Response from 'nfc' is like : { key: { type: 'xxx', code: '89792384'}, pass: { link_type: 'direct', link_url: 'http...' }}
      // Response from 'nfcLink' is like : { link_url: 'http...' }
      put('nfc', payload)
        .then(res => {
          setToken(res)
          if (res && res.pass && res.pass.link_type) {
            setSelectedWallet(res.pass.link_type)
            setSection('download')
            logEvent('activated_nfc_access')
          }
        })
        .catch(err => setError(err))
        .finally(() => {
          setIsSubmitting(false)
        })
    } else {
      get('nfcLink')
        .then(res => {
          setToken(res)
          if (res && res.link_url) {
            setSection('redirect')
            logEvent('fetched_nfc_link')
          }
        })
        .catch(err => setError(err))
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  }

  const sendSms = (event) => {
    if (isSubmitting) return

    if (!token && !token.pass && !token.pass.link_url) {
      toast.error(t(phrase('redirect.link_error')))
      return
    }

    setIsSubmitting(true)

    put('nfcSms', null)
      .then(res => {
        logEvent('request_nfc_sms')
        if (res.sent) {
          toast.success(t(phrase('redirect.sms_ok')))
        } else {
          toast.error(t(phrase('redirect.sms_error')))
        }
      })
      .catch(err => {
        toast.error(t(phrase('redirect.sms_error')))
        console.error(err)
      })
      .finally(() => {
        setTimeout(() => {
          setIsSubmitting(false)
        }, 5000)
      })
  }

  const checkWalletStatus = async () => {
    try {
      const response = await get(`/v1/membership/nfc/${token.key.id}/status`)

      return response.wallet_status
    } catch (error) {
      console.error('Failed checking wallet status', error)
    }
  }

  const stopPolling = () => {
    pollStoppedRef.current = true
    setIsSubmitting(false)

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }

  const poll = async (attempt = 1) => {
    if (pollStoppedRef.current) {
      return
    }

    try {
      const status = await checkWalletStatus()

      if (status === 'loaded') {
        stopPolling()
        setSection('success')
        return
      }

      if (attempt >= maxPolls) {
        stopPolling()
        setSection('unknown')
        return
      }

      pollTimeoutRef.current = setTimeout(() => poll(attempt + 1), 2000)
    } catch (error) {
      console.error(error)

      pollTimeoutRef.current = setTimeout(() => poll(attempt + 1), 2000)
    }
  }

  const startPolling = () => {
    // Prevent multiple polling loops
    stopPolling()

    if (isSubmitting) return
    setIsSubmitting(true)

    pollStoppedRef.current = false

    pollTimeoutRef.current = setTimeout(() => poll(1), 5000)
  }

  const activateButton = () => {
    return (
      <React.Fragment>
        <h4>{t(phrase(device.isMobile ? 'activate.download' : 'activate.remote'))}</h4>
        <ActionButton
          type={'submit'}
          disabled={isSubmitting}
          value={`${device.isMobile ? (device.isAndroid ? 'google' : 'apple') : 'unknown'}`}
        >{t(phrase('activate.activate'))}</ActionButton>
      </React.Fragment>
    )
  }

  const walletType = !device.isMobile
    ? 'unknown'
    : device.isAndroid
      ? 'google'
      : 'apple'

  const walletName = t(phrase(`activate.wallet.${walletType}`))

  const renderActivate = () => {
    let paragraphs
    try {
      paragraphs = t(phrase('activate.how_it_works'), { wallet_type: walletName, returnObjects: true })
    } catch (err) {
      console.error(err)
      paragraphs = [`Malformed translation for ${phrase('activate.how_it_works')}`]
    }

    // As a safety precaution, checking that steps is correctly converted to
    // an array, or it throws an error when doing steps.map
    if (!Array.isArray(paragraphs)) {
      setError('translation_error')
      return null
    }

    return (
      <React.Fragment>
        <h2>{t(phrase('activate.title'))}</h2>
        <h4>{t(phrase('activate.how_title'))}</h4>
        <img src={`/images/how_it_works_${country}.png`} alt="HowItWorks" className='picture' />
        {paragraphs.map((para, idx) => <p className='paragraph' key={idx}>{para}</p>)}
        {rfid && <p className='warn'>{t(phrase('activate.disable_rfid'))}</p>}
        {nfc && <p className='warn'>{t(phrase('activate.disable_nfc'))}</p>}
        <form onSubmit={onSubmit}>
          {activateButton()}
        </form>
      </React.Fragment>
    )
  }

  const renderLoadWallet = () => {
    // const link = token.pass.link_url
    const phone = device.isApple ? 'iphone' : 'android'
    const wallet = device.isApple ? 'apple' : 'google'

    let steps
    try {
      steps = t(phrase(['download', 'how_steps', wallet]), { returnObjects: true })
    } catch (err) {
      console.error(err)
      steps = [`Malformed translation for ${phrase(['download', 'how_steps', wallet])}`]
    }

    // As a safety precaution, checking that steps is correctly converted to
    // an array, or it throws an error when doing steps.map
    if (!Array.isArray(steps)) {
      setError('translation_error')
      return null
    }

    return (
      <div className="activate download">
        <h2>{t(phrase('download.title_full'))}</h2>
        <h4>{t(phrase('download.how_title'))}</h4>
        <div className='crop'>
          <img className={`crop ${phone}`} src={`/images/download_${phone}_${country}.png`} alt={t(phrase('download.add_to_wallet'))}/>
        </div>
        <ol>
          {steps.map((step, idx) => <li key={idx}
            dangerouslySetInnerHTML={{
              __html: t(step),
            }}></li>)}
        </ol>
        {device.isAndroid
          ? <GoogleButton token={token} locale={locale} t={t} action={startPolling} />
          : <AppleButton token={token} locale={locale} t={t} action={startPolling} />}
        {backButton(goBack, 'back')}
      </div>
    )
  }

  const renderQrCode = () => {
    const link = token.link_url

    if (!link) {
      setError('missing_link')
    }

    let steps
    try {
      steps = t(phrase(['redirect', 'how_steps']), { returnObjects: true })
    } catch (err) {
      console.error(err)
      steps = [`Malformed translation for ${phrase(['redirect', 'how_steps'])}`]
    }

    // As a safety precaution, checking that steps is correctly converted to
    // an array, or it throws an error when doing steps.map
    if (!Array.isArray(steps)) {
      setError('translation_error')
      return null
    }

    return (
      <div className="activate redirect">
        <h1>{t(phrase('redirect.title'))}</h1>
        <h3>{t(phrase('redirect.how_title'))}</h3>
        <ol>
          {steps.map((step, idx) => <li key={idx}
            dangerouslySetInnerHTML={{
              __html: t(step),
            }}></li>)}
        </ol>
        <QrCodeSvg value={link} />

        <p>{t(phrase('redirect.sms_receive'))}&nbsp;
          <button id="sms-button"
            className='link-button'
            onClick={sendSms}
            disabled={isSubmitting}>{t(phrase('redirect.sms_click'))}
          </button>
        </p>
        {backButton(goBack, 'back')}
      </div>
    )
  }

  const renderHelp = () => {
    return (
      <React.Fragment>
        <h4>{t(phrase('download.help.header'))}</h4>
        <p>{t(phrase('download.help.problem'))}</p>
        <ul>
          {['sms', 'support'].map((key) => <li key={key}
            dangerouslySetInnerHTML={{ __html: t(phrase(`download.help.${key}`)) }}></li>,
          )}
        </ul>
      </React.Fragment>
    )
  }

  const renderSuccess = () => {
    const walletPhrase = device.isApple ? 'apple' : 'google'
    const wallet = t(phrase(`activate.wallet.${walletPhrase}`))

    return (
      <div className="activate result">
        <div>
          <div className='check'><p>✓</p></div>
        </div>
        <h3>{t(phrase('success.header'), { wallet: wallet })}</h3>
        <img src={`/images/how_it_works_${country}.png`} alt="HowItWorks" className='picture' />
        <p>{t(phrase('success.use'))}</p>
        {backButton(goMyPage, 'back_mypage')}
        {renderHelp()}
      </div>
    )
  }

  const renderUnknown = () => {
    const phone = device.isApple ? 'iphone' : 'android'
    const walletPhrase = device.isApple ? 'apple' : 'google'
    const wallet = t(phrase(`activate.wallet.${walletPhrase}`))

    return (
      <div className="activate result">
        <div>
          <div className='check failure'>✕</div>
        </div>
        <h3>{t(phrase('unknown.header'))}</h3>
        <div className='crop'>
          <img className={`crop ${phone}`} src={`/images/download_${phone}_${country}.png`} alt={t(phrase('download.add_to_wallet'))}/>
        </div>
        <p>{t(phrase('unknown.not_loaded'), { wallet: wallet })}</p>
        <p>{t(phrase('unknown.if_ok'))}</p>
        <p dangerouslySetInnerHTML={{ __html: t(phrase('unknown.try_again')) }}></p>
        {backButton(() => goBack('download'), 'retry')}
        {backButton(goMyPage, 'back_mypage')}
        {renderHelp()}
      </div>
    )
  }

  const backButton = (action, phraseKey) => {
    return (
      <React.Fragment>
        <ActionButton type='button' className='action-button'
          onClick={action}>{t(phrase([section, phraseKey]))}</ActionButton>
      </React.Fragment>
    )
  }

  const goBack = (toSection = 'activate') => {
    setSuccess(null)
    setError(null)
    if (section !== toSection) {
      setSection(toSection)
    }
  }

  const goMyPage = () => {
    history.go(-2)
  }

  let renderer = renderActivate

  if (section !== 'activate') {
    if (section === 'redirect') {
      renderer = renderQrCode
    } else if (section === 'download') {
      renderer = renderLoadWallet
    } else if (section === 'success') {
      renderer = renderSuccess
    } else if (section === 'unknown') {
      renderer = renderUnknown
    } else {
      // just in case we don't have a valid token
      setSection('activate')
    }
  }

  if (error) {
    return <StandardErrorPage error={error} goBack={goBack} />
  }

  return (
    <div className='activate'>
      <section className={`loading ${isSubmitting ? '' : 'invisible'}`}>
        <div className={`centred ${isSubmitting ? '' : 'invisible'}`}><img src="/images/LoaderCredlock.gif" alt="processing..." width="62" height="62"/></div>
      </section>
      {renderer()}
      <ToastContainer
        position='bottom-center'
        autoClose={2500}
        closeOnClick
        closeButton={false}
        draggable
        hideProgressBar
        pauseOnHover />
    </div>
  )
}

export default ActivateNFC
